import { useEffect, useRef } from "react";

interface ParallaxCoverProps {
  src: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
  className?: string;
}

/**
 * Edge-to-edge cover image with a 50% parallax shift and a subtle scroll-driven
 * zoom-in. Uses a rAF-throttled scroll listener and honours reduced-motion.
 */
export const ParallaxCover = ({ src, srcSet, sizes, alt, className }: ParallaxCoverProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      const wrapper = wrapperRef.current;
      const img = imgRef.current;
      if (!wrapper || !img) return;

      const rect = wrapper.getBoundingClientRect();
      // Progress from 0 (wrapper top at viewport top) to 1 (wrapper fully scrolled past)
      const scrolled = Math.min(Math.max(-rect.top, 0), rect.height);
      const progress = rect.height > 0 ? scrolled / rect.height : 0;

      const shift = scrolled * 0.5; // 50% parallax
      const scale = 1.15 + progress * 0.15; // zoom-in while scrolling

      img.style.transform = `translate3d(0, ${shift}px, 0) scale(${scale})`;
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`relative z-10 overflow-hidden border-y border-border ${className ?? ""}`}
    >
      <img
        ref={imgRef}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        width={1600}
        height={630}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        className="h-full w-full object-cover will-change-transform scale-[1.15] motion-reduce:scale-100"
      />
    </div>
  );
};
